import { Suspense, lazy } from "react";
import { Spinner } from "@ui";
import type { Message } from "@agentic_conversation/pages/chat/types";
import { defaultRemarkPlugins } from "streamdown";
import { markdownComponents } from "./markdown-components";
import { remarkChart } from "./custom/chart/remark-chart";
import { formatDate } from "@ui";
import { StreamingErrorBoundary } from "./error-boundary";

const Streamdown = lazy(() =>
  import("streamdown").then((m) => ({ default: m.Streamdown }))
);

// Streamdown's `defaultRemarkPlugins` is a keyed object ({ gfm: [...], ... });
// internally Streamdown applies `Object.values` of it. We replicate that so
// GFM tables keep working, then append the ```chart → <chart> transform.
const remarkPlugins = [
  ...Object.values(defaultRemarkPlugins ?? {}),
  remarkChart,
];

interface SynthesizerProps {
  message: Message;
}

export function Synthesizer({ message }: SynthesizerProps) {
  const isLoading =
    message.status === "progress" || message.status === "pending";
  const isError = message.status === "error";

  return (
    <div className="flex flex-col space-y-3">
      {/* Loading state */}
      {isLoading && !message.content && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Spinner size="sm" />
          <span>Réponse en cours...</span>
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="text-sm text-red-600">Erreur lors de la réponse</div>
      )}

      {/* Content - hidden when in error state (content would be a raw exception message) */}
      {message.content && !isError && (
        <div className="text-slate-600 max-w-none">
          <StreamingErrorBoundary>
            <Suspense fallback={<div className="animate-pulse">Chargement...</div>}>
              <Streamdown
                components={markdownComponents}
                remarkPlugins={remarkPlugins}
                isAnimating={isLoading}
                controls={{ code: true, table: false }}
              >
                {message.content}
              </Streamdown>
            </Suspense>
          </StreamingErrorBoundary>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div>{message.content && isLoading && <Spinner size="xs" />}</div>
        <div className="text-[11px] text-slate-400">
          {formatDate(message.created_at)} (#{message.id})
        </div>
      </div>
    </div>
  );
}
